import { z } from 'zod';
import { emailSchema, passwordSchema, personNameSchema } from './accounts.js';

/** BR-07: role łączą się; opiekun to każde konto (bez osobnej roli). */
export const roles = ['admin', 'staff', 'speaker'] as const;
export const roleSchema = z.enum(roles);
export type Role = z.infer<typeof roleSchema>;

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, { message: `Maksymalnie ${max} znaków.` })
    .optional()
    .transform((value) => (value ? value : undefined));

const tokenSchema = z.string().trim().min(20).max(200);

// --- Zgody (US-211) ---

export const consentCodes = ['data_processing', 'image_publication', 'newsletter'] as const;
export const consentCodeSchema = z.enum(consentCodes);
export type ConsentCode = z.infer<typeof consentCodeSchema>;

export const currentConsentSchema = z.object({
  versionId: z.string().uuid(),
  code: consentCodeSchema,
  version: z.number().int(),
  required: z.boolean(),
  title: z.string(),
  content: z.string(),
});
export type CurrentConsent = z.infer<typeof currentConsentSchema>;
/**
 * Zgody do formularza rejestracji i klauzula informacyjna pod formularzem (`notice` – HTML oczyszczony przez API przy zapisie,
 * edytowalny w panelu admina; decyzja 2026‑09‑28, PRD §10 pkt 35).
 */
export const currentConsentsResponseSchema = z.object({
  consents: z.array(currentConsentSchema),
  notice: z.string().nullable().optional(),
});

/**
 * Zgoda w panelu admina. Bez wersjonowania (decyzja 2026‑09‑28, PRD §10 pkt 35) – treść poprawia się w miejscu.
 * `accepted` – ile kont ją zaakceptowało.
 */
export const adminConsentSchema = z.object({
  code: consentCodeSchema,
  title: z.string(),
  required: z.boolean(),
  content: z.string(),
  accepted: z.number().int(),
});
export type AdminConsent = z.infer<typeof adminConsentSchema>;
export const adminConsentListSchema = z.object({ consents: z.array(adminConsentSchema), notice: z.string().nullable() });

/** Treść zgody – zwykły tekst. */
export const consentContentSchema = z.object({ content: z.string().trim().min(10).max(10_000) });

/** Klauzula informacyjna pod formularzem rejestracji – HTML z edytora (API go czyści). Pusta = bez klauzuli. */
export const registrationNoticeSchema = z.object({ html: z.string().max(20_000) });
export type RegistrationNotice = z.infer<typeof registrationNoticeSchema>;

// --- Rejestracja i aktywacja (US-201 … US-203) ---

/** Ścieżka powrotu w aplikacji (US-902 AC-2): tylko nasza ścieżka, bez innej domeny (`//…`, `/\…`). */
export const returnPathSchema = z.string().max(500).regex(/^\/(?![/\\])/);

export const registerRequestSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  firstName: personNameSchema,
  lastName: personNameSchema,
  school: optionalText(200),
  referralSource: optionalText(200),
  /** Wersje zgód, które użytkownik zaakceptował (te, które widział w formularzu). */
  acceptedConsentVersionIds: z.array(z.string().uuid()).max(10),
  /** US-409 AC-2: rejestracja rozpoczęta z linku wczesnego dostępu – dostęp trafia do konta od razu (każde urządzenie). */
  earlyAccessToken: tokenSchema.optional(),
  /** US-902 AC-2: po potwierdzeniu e‑maila powrót do agendy lub zajęć, z których zaczęła się rejestracja. */
  returnTo: returnPathSchema.optional(),
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const emailOnlyRequestSchema = z.object({ email: emailSchema });
export type EmailOnlyRequest = z.infer<typeof emailOnlyRequestSchema>;

export const tokenRequestSchema = z.object({ token: tokenSchema });
export type TokenRequest = z.infer<typeof tokenRequestSchema>;

// --- Logowanie (US-204) ---

export const loginRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

// --- Hasło (US-206, US-207) ---

export const setPasswordRequestSchema = z.object({ token: tokenSchema, password: passwordSchema });
export type SetPasswordRequest = z.infer<typeof setPasswordRequestSchema>;

export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: passwordSchema,
});
export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;

// --- Dane konta (US-208) ---

export const updateAccountRequestSchema = z.object({
  firstName: personNameSchema,
  lastName: personNameSchema,
  school: optionalText(200),
  referralSource: optionalText(200),
  /**
   * PRD §10 pkt 61: właściciel konta‑uczeń podaje rok urodzenia – jego osoba jest wtedy dzieckiem (reguły wieku jak u dziecka).
   * `null` = dorosły; brak pola = bez zmiany. Zakres lat sprawdza serwer (jak przy dziecku, US-402 AC-2).
   */
  birthYear: z
    .preprocess(
      (value) => (value === '' || value === null ? null : value === undefined ? undefined : Number(value)),
      z
        .number({ message: 'Podaj rok, np. 2012.' })
        .int({ message: 'Podaj rok, np. 2012.' })
        .min(1900, { message: 'Podaj rok, np. 2012.' })
        .max(2200, { message: 'Podaj rok, np. 2012.' })
        .nullable()
        .optional(),
    ),
});
export type UpdateAccountRequest = z.infer<typeof updateAccountRequestSchema>;

export const meResponseSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  school: z.string().nullable(),
  referralSource: z.string().nullable(),
  /** PRD §10 pkt 61: rok urodzenia właściciela‑ucznia; `null` = dorosły (także przed potwierdzeniem e‑maila). */
  birthYear: z.number().int().nullable(),
  roles: z.array(roleSchema),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

/** Odpowiedź na żądania, które celowo nie zdradzają, czy konto istnieje (Z-201, BR-05). */
export const acceptedResponseSchema = z.object({ status: z.literal('accepted') });

// --- Role i zaproszenia (US-209) ---

export const adminAccountSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  roles: z.array(roleSchema),
  emailVerified: z.boolean(),
  passwordSet: z.boolean(),
});
export type AdminAccount = z.infer<typeof adminAccountSchema>;
export const adminAccountListSchema = z.object({ accounts: z.array(adminAccountSchema) });

/** Lista kont dla admina w podziale na role: `none` – konta bez ról (rodziny), `all` – wszystkie. */
export const accountRoleFilters = ['all', 'admin', 'staff', 'speaker', 'none'] as const;
export const accountRoleFilterSchema = z.enum(accountRoleFilters);
export type AccountRoleFilter = z.infer<typeof accountRoleFilterSchema>;
export const ADMIN_ACCOUNTS_PAGE_SIZE = 50;

export const adminAccountPageSchema = z.object({
  accounts: z.array(adminAccountSchema),
  /** Liczba kont w wybranej zakładce (po wyszukiwaniu) – do stronicowania. */
  total: z.number().int(),
  /** Liczby kont w zakładkach (bez wyszukiwania). Konto z kilkoma rolami liczy się w każdej z nich. */
  counts: z.record(accountRoleFilterSchema, z.number().int()),
});
export type AdminAccountPage = z.infer<typeof adminAccountPageSchema>;

export const setRolesRequestSchema = z.object({ roles: z.array(roleSchema).max(roles.length) });
export type SetRolesRequest = z.infer<typeof setRolesRequestSchema>;

export const inviteRequestSchema = z.object({
  email: emailSchema,
  firstName: personNameSchema,
  lastName: personNameSchema,
  roles: z.array(roleSchema).min(1).max(roles.length),
});
export type InviteRequest = z.infer<typeof inviteRequestSchema>;
